"use client";
import * as React from "react";
import { Brain, Cloud, Shield, BarChart3, Zap, Code2 } from "lucide-react";

function ModernFeatures() {
  const features = [
    {
      icon: Brain,
      title: "AI-Powered Automation",
      description:
        "Intelligent automation that learns from your workflows and optimizes processes in real-time.",
      metrics: ["99.9% Accuracy", "Real-time Learning", "Smart Optimization"],
    },
    {
      icon: Cloud,
      title: "Cloud-Native Infrastructure",
      description:
        "Scalable, secure, and resilient cloud architecture designed for enterprise performance.",
      metrics: ["Auto-scaling", "99.99% Uptime", "Global Edge Network"],
    },
    {
      icon: Shield,
      title: "Enterprise Security",
      description:
        "Bank-grade security with zero-trust architecture and end-to-end encryption.",
      metrics: ["Zero-Trust Model", "256-bit Encryption", "SOC 2 Compliant"],
    },
    {
      icon: BarChart3,
      title: "Advanced Analytics",
      description:
        "Transform data into actionable insights with AI-powered analytics and visualization.",
      metrics: ["Predictive Models", "Real-time Dashboards", "Custom Reports"],
    },
    {
      icon: Zap,
      title: "Performance Optimization",
      description:
        "Cutting-edge performance monitoring with automatic optimization and load balancing.",
      metrics: ["Sub-second Response", "Auto-optimization", "Smart Caching"],
    },
    {
      icon: Code2,
      title: "Developer Experience",
      description:
        "Comprehensive APIs, SDKs, and tools designed for seamless integration and development.",
      metrics: ["RESTful APIs", "SDK Libraries", "24/7 Support"],
    },
  ];

  return (
    <section
      className="relative py-24 overflow-hidden"
      role="main"
      aria-labelledby="features-heading"
    >
      {/* Top Wave */}
      <div className="absolute top-0 left-0 right-0" aria-hidden="true">
        <svg viewBox="0 0 1200 120" className="w-full h-auto">
          <path
            d="M0,60 C300,0 500,120 800,60 C1000,20 1100,80 1200,60 L1200,0 L0,0 Z"
            fill="#F5F7FA"
          />
        </svg>
      </div>

      {/* Background Blobs */}
      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
        <div
          className="absolute top-20 right-10 w-96 h-96 rounded-full opacity-30 blur-3xl"
          style={{
            background: "radial-gradient(circle, #E8ECFF 0%, transparent 70%)",
          }}
        ></div>
        <div
          className="absolute bottom-40 left-20 w-80 h-80 rounded-full opacity-20 blur-2xl"
          style={{
            background: "radial-gradient(circle, #1719A8 0%, transparent 70%)",
          }}
        ></div>
        <div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full opacity-10 blur-xl"
          style={{
            background: "radial-gradient(circle, #D96F2B 0%, transparent 70%)",
          }}
        ></div>
      </div>

      {/* Floating Abstract Circles */}
      <div className="absolute inset-0" aria-hidden="true">
        <div className="absolute top-32 left-1/4 w-16 h-16 bg-white/20 rounded-full backdrop-blur-sm animate-float"></div>
        <div className="absolute bottom-1/3 right-1/4 w-12 h-12 bg-white/30 rounded-full backdrop-blur-sm animate-float-delayed"></div>
        <div className="absolute top-2/3 left-3/4 w-20 h-20 bg-white/10 rounded-full backdrop-blur-sm animate-float"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <header className="text-center mb-6">
          <div className="inline-flex items-center px-4 py-2 lg:px-6 lg:py-2 md:px-4 md:py-1.5 sm:px-3 sm:py-1 bg-[#1719A8]/90 backdrop-blur-md border border-[#1719A8]/60 rounded-full mb-3 shadow-lg">
            <span
              className="w-2 h-2 bg-white rounded-full mr-3 animate-pulse"
              aria-hidden="true"
            ></span>
            <span className="text-xs md:text-sm lg:text-sm sm:text-xs text-white font-semibold uppercase tracking-wider">
              Platform Features
            </span>
          </div>

          <h1
            id="features-heading"
            className="text-5xl lg:text-6xl font-black mb-8 tracking-tight"
            style={{ color: "#1E293B" }}
          >
            Built for the
            <span
              className="block bg-gradient-to-r bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(135deg, #1719A8 0%, #D96F2B 50%, #C9C51A 100%)",
              }}
            >
              Future of Work
            </span>
          </h1>

          <p
            className="text-xl leading-relaxed max-w-3xl mx-auto"
            style={{ color: "#64748B" }}
          >
            Experience next-generation enterprise technology with features
            designed to accelerate innovation and drive unprecedented business
            growth.
          </p>
        </header>

        {/* Features Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-20" role="list">
          {features.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <article
                key={index}
                className="group relative bg-white/40 backdrop-blur-xl rounded-3xl border border-white/30 hover:bg-white/60 transition-all duration-500 hover:scale-105 hover:shadow-2xl overflow-hidden"
                style={{ boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.1)" }}
                role="listitem"
              >
                {/* Card Glow Effect */}
                <div
                  className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  aria-hidden="true"
                ></div>

                <div className="relative p-8">
                  <div className="flex items-center mb-6">
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform duration-500   border-white/30"
                      style={{
                        background: "linear-gradient(135deg, #1719A8, #3B82F6)",
                      }}
                      aria-hidden="true"
                    >
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                  </div>

                  <h2 className="text-2xl font-bold mb-4 text-[#1E293B] transition-all duration-300 group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:text-blue-800">
                    {feature.title}
                  </h2>

                  <p
                    className="leading-relaxed mb-6"
                    style={{ color: "#64748B" }}
                  >
                    {feature.description}
                  </p>

                  <ul className="space-y-3" role="list">
                    {feature.metrics.map((metric, metricIndex) => (
                      <li key={metricIndex} className="flex items-center">
                        <div
                          className="w-2 h-2 bg-[#1719A8] rounded-full mr-3"
                          aria-hidden="true"
                        ></div>
                        <span
                          className="text-sm font-medium"
                          style={{ color: "#1E293B" }}
                        >
                          {metric}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Bottom Accent Line */}
                <div
                  className="absolute bottom-0 left-0 right-0 h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"
                  style={{
                    background:
                      "linear-gradient(90deg, #1719A8, #D96F2B, #C9C51A)",
                  }}
                  aria-hidden="true"
                ></div>
              </article>
            );
          })}
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0" aria-hidden="true">
        <svg viewBox="0 0 1200 120" className="w-full h-auto">
          <path
            d="M0,60 C300,120 500,0 800,60 C1000,100 1100,40 1200,60 L1200,120 L0,120 Z"
            fill="#FFFFFF"
          />
        </svg>
      </div>

      {/* Custom Animations */}
      <style>{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(5deg);
          }
        }
        @keyframes float-delayed {
          0%,
          100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-15px) rotate(-3deg);
          }
        }
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 10s ease-in-out infinite 3s;
        }
      `}</style>
    </section>
  );
}

export default ModernFeatures;
